import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useAccount } from '@/account/useAccount';
import { useAppConstantsContext } from '@/context/AppConstants';
import { useReportContext } from '@/context/ReportContext';
import { useOram } from '@/myGarage/useOram';
import { useProcessClaim } from '@/reports/shared/Claiming/useProcessClaim';
import { ReportSectionStyled } from '@/reports/shared/ReportSection/ReportSection';
import { Meta } from '@/reports/types/report';
import {
  Box,
  Button,
  Card,
  PureTextButton,
  Icons,
  Stack,
  Text,
  styled,
} from '@/theme/index';
import { checkSingleReportVariations } from '@/utils/checkSingleReportVariations';
import { useRedirect } from '@/utils/hooks/useRedirect';
import { useScreenSize } from '@/utils/useScreenSize';

interface CardProps {
  image: string | undefined;
  icon?: React.ReactNode;
  outlinedButton?: boolean;
  title: string;
  content: string;
  buttonText: string;
  onClick: () => void;
}

interface GetMostOutProps {
  sectionId?: string;
  claimed: boolean;
  meta?: Meta | null;
}

const CardImage = styled('img')(({ theme }) => ({
  width: '55px',
  height: '55px',

  [theme.breakpoints.down('sm')]: {
    width: '45px',
    height: '45px',
  },
}));

const CustomCard = ({
  image,
  icon,
  outlinedButton,
  title,
  content,
  buttonText,
  onClick,
}: CardProps) => {
  const { isMobile } = useScreenSize();

  return (
    <Card variant="outlined">
      <Stack direction="row" display="flex" alignItems="center">
        <Box
          mb={isMobile ? 2 : 0}
          mr={isMobile ? 1.5 : 3}
          data-testid="my-garage-cta-image"
        >
          <CardImage src={image} alt="Cta" />
        </Box>
        <Stack
          direction={isMobile ? 'column' : 'row'}
          display="flex"
          alignItems={isMobile ? 'flex-start' : 'center'}
        >
          <Stack
            direction="column"
            sx={{ width: { sm: '100%', md: '60%' } }}
            alignItems="flex-start"
          >
            <Text
              variant={isMobile ? 'body1' : 'h3'}
              display="flex"
              alignItems="center"
            >
              {icon && icon}
              {title}
            </Text>
            {!isMobile && (
              <Text variant="body1" sx={{ mb: 1 }}>
                {content}
              </Text>
            )}
          </Stack>
          {isMobile ? (
            <PureTextButton sx={{ p: 0 }} onClick={onClick} color="success">
              {buttonText}
            </PureTextButton>
          ) : (
            <Button
              variant={outlinedButton ? 'outlined' : 'contained'}
              color="success"
              onClick={onClick}
              sx={{ ml: isMobile ? 0 : 3, minWidth: '180px' }}
            >
              {buttonText}
            </Button>
          )}
        </Stack>
      </Stack>
    </Card>
  );
};

export const GetMostOut = ({ sectionId, claimed, meta }: GetMostOutProps) => {
  const {
    data: { brandName },
    images: { myGarageCtaImage, rewardsCtaImage },
    variationPlanNames,
    internationalPlanNames,
  } = useAppConstantsContext();
  const { createTrackedVehicleMutation } = useOram();
  const queryClient = useQueryClient();
  const { redirect } = useRedirect();
  const { permalink } = useReportContext();
  const { mutate: handleClaim, isSuccess } = useProcessClaim();
  const [claimedVehicle, setClaimedVehicle] = useState(claimed);

  const { data: account } = useAccount(true);

  const reportVariations =
    account && internationalPlanNames
      ? checkSingleReportVariations(
          account,
          variationPlanNames,
          internationalPlanNames
        )
      : null;

  const isInInternationalLanguageTest =
    reportVariations?.isInInternationalLanguageTest ?? false;
  const isInternationalVinTest =
    reportVariations?.isInternationalVinTest ?? false;

  const isYMMSearch = meta?.search_by === 'ymm';

  const handleClickMyGarage = () => {
    if (claimedVehicle) {
      return redirect('/dashboard/my-garage');
    }

    const formData = {
      report: {
        tag: 'self',
        permalink,
      },
    };

    handleClaim(formData);
    handleCreateTrackedVehicle();
  };

  if (!claimedVehicle && isSuccess) {
    setClaimedVehicle(true);
    // TODO: we need to investigate the useMemo in the vehicle report
    // since we use isLoading instead of isInitialLoading when we invalidate the report to get
    // new claimed state from the server we wipe the whole report from the dom
    // another option is to improve our useProcessClaim hook to return the new claimed state
    queryClient.invalidateQueries(['report']);
  }

  const handleCreateTrackedVehicle = () => {
    if (meta?.search_by === 'vin') {
      createTrackedVehicleMutation.mutate({
        vin: meta.search_data.vin,
      });
    }

    if (meta?.search_by === 'plate') {
      createTrackedVehicleMutation.mutate({
        license_plate: meta.search_data.license_plate,
        state: meta.search_data.state,
      });
    }
  };

  return (
    <ReportSectionStyled id={sectionId}>
      <Text variant="h1" color="primary.dark">
        Get the most out of {brandName}
      </Text>
      {!isYMMSearch && (
        <CustomCard
          image={myGarageCtaImage}
          icon={
            <Icons.Check color="success" fontSize="small" sx={{ mr: 0.5 }} />
          }
          outlinedButton={claimedVehicle}
          title="Is this your vehicle?"
          content="Stay on top of service needs, vehicle history changes and much more with My Garage"
          buttonText={claimedVehicle ? 'View My Garage' : 'Add to My Garage'}
          onClick={handleClickMyGarage}
        />
      )}

      {!isInInternationalLanguageTest && !isInternationalVinTest && (<CustomCard
        image={rewardsCtaImage}
        title={`Save time and money with ${brandName} Member Perks`}
        content="Access thousands in rewards"
        buttonText="Learn More"
        onClick={() => redirect('/dashboard/rewards')}
      />)}
    </ReportSectionStyled>
  );
};
